/**
 * The page's answer to a session that ended under it: a burst of parallel 401s gets one
 * sign-out, one toast and one trip to the login page.
 */

import type { AuthSession } from '@/auth/authSession';
import { loginNextTarget } from '@/auth/loginNext';
import { SECOND_MS } from '@/lib/timeUnits';

// Long enough for the toast to show before the page unloads.
const LOGIN_REDIRECT_DELAY_MS = 250;
// The latch re-arms: when a redirect does not land, the next 401 must try it again.
const REDIRECT_ONCE_MS = 10 * SECOND_MS;
const EXPIRED_MESSAGE = 'Session expired. Redirecting to login…';
// Vite's `base` ('/ui/' in the bundle that the cloud Go service serves), so that the redirect
// reaches the app's /login route.
const APP_BASE = (import.meta.env?.BASE_URL || '/').replace(/\/+$/, '');

type PageLocation = Pick<Location, 'assign' | 'pathname' | 'search'>;

interface SessionExpiryDeps {
    authSession: Pick<AuthSession, 'clearSession'>;
    /** The page's location; null where there is no page. */
    getLocation: () => PageLocation | null;
    showToast: (message: string) => void;
    schedule: (callback: () => void, delayMs: number) => void;
}

/** `/setup-server` gives way to its own next: see auth/loginNext.ts. */
function getLoginUrl(location: PageLocation): string {
    const nextTarget = loginNextTarget(location.pathname, location.search);
    return `${APP_BASE}/login?next=${encodeURIComponent(nextTarget)}`;
}

export class SessionExpiry {
    private readonly authSession: Pick<AuthSession, 'clearSession'>;

    private readonly getLocation: () => PageLocation | null;

    private readonly showToast: (message: string) => void;

    private readonly schedule: (callback: () => void, delayMs: number) => void;

    private isRedirecting = false;

    constructor(deps: SessionExpiryDeps) {
        this.authSession = deps.authSession;
        this.getLocation = deps.getLocation;
        this.showToast = deps.showToast;
        this.schedule = deps.schedule;
    }

    handle(): void {
        if (this.isRedirecting) {
            return;
        }
        this.isRedirecting = true;
        this.schedule((): void => {
            this.isRedirecting = false;
        }, REDIRECT_ONCE_MS);
        const location = this.getLocation();
        // Read before the sign-out: AuthGate answers it by moving the page to /login, and a
        // target read after that puts the login page into its own `next`.
        const loginUrl = location === null ? null : getLoginUrl(location);
        this.authSession.clearSession();
        this.showToast(EXPIRED_MESSAGE);
        if (location === null || loginUrl === null) {
            return;
        }
        this.schedule((): void => {
            location.assign(loginUrl);
        }, LOGIN_REDIRECT_DELAY_MS);
    }
}
